import instance, { ADMIN_PREFIX } from 'services/api';
import {
  FeatureItemDetail,
  FeatureItemInput,
  FeatureItemListResponse,
  FeatureItemRow,
  SubscriptionDetail,
  SubscriptionFeatureListRow,
  SubscriptionInput,
  SubscriptionListResponse
} from 'types/api/subscriptions';

/**
 * GET /api/v2/admin/subscriptions/list/admin — paginated subscriptions list.
 * BE already snake_cases the keys via toSnake() (cycle-16). FE receives
 * {results, total} envelope directly.
 */
export const fetchSubscriptionPlans = async (
  page = 0,
  limit = 999
): Promise<SubscriptionListResponse> => {
  const res = await instance.get<SubscriptionListResponse>(
    `${ADMIN_PREFIX}/subscriptions/list/admin`,
    { params: { page, limit } }
  );

  return {
    results: res.data.results ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/subscriptions/list/patient/:id — admin detail.
 * Cycle-16 reshaped to legacy {id, name, icon, features[], options[]}
 * (combineFeatures + prices pivot under the hood). Path label is a
 * legacy quirk — kept as-is.
 */
export const fetchSubscriptionPlanById = async (
  id: string | number
): Promise<SubscriptionDetail> => {
  const res = await instance.get<SubscriptionDetail>(
    `${ADMIN_PREFIX}/subscriptions/list/patient/${id}`
  );

  return res.data;
};

export const createSubscriptionPlan = (input: SubscriptionInput) =>
  instance.post<SubscriptionDetail>(
    `${ADMIN_PREFIX}/subscriptions/list`,
    input
  );

export const updateSubscriptionPlan = (
  id: string | number,
  input: SubscriptionInput
) =>
  instance.put<SubscriptionDetail>(
    `${ADMIN_PREFIX}/subscriptions/list/${id}`,
    input
  );

export const deleteSubscriptionPlan = (id: string | number) =>
  instance.delete<{ status: boolean }>(
    `${ADMIN_PREFIX}/subscriptions/list/${id}`
  );

// ── Feature items ────────────────────────────────────────────────────────

export const fetchFeatureItems = async (
  page = 0,
  limit = 10
): Promise<FeatureItemListResponse> => {
  const res = await instance.get<FeatureItemListResponse>(
    `${ADMIN_PREFIX}/subscriptions/feature-items`,
    { params: { page, limit } }
  );

  return {
    results: res.data.results ?? [],
    total: res.data.total ?? 0
  };
};

export const fetchFeatureItemById = async (
  id: string | number
): Promise<FeatureItemDetail> => {
  const res = await instance.get<FeatureItemDetail>(
    `${ADMIN_PREFIX}/subscriptions/feature-items/${id}`
  );

  return res.data;
};

export const createFeatureItem = (input: FeatureItemInput) =>
  instance.post<FeatureItemRow>(
    `${ADMIN_PREFIX}/subscriptions/feature-items`,
    input
  );

export const updateFeatureItem = (
  id: string | number,
  input: FeatureItemInput
) =>
  instance.put<FeatureItemRow>(
    `${ADMIN_PREFIX}/subscriptions/feature-items/${id}`,
    input
  );

export const deleteFeatureItem = (id: string | number) =>
  instance.delete<{ status: boolean }>(
    `${ADMIN_PREFIX}/subscriptions/feature-items/${id}`
  );

// ── Features (static catalog) ────────────────────────────────────────────

export const fetchSubscriptionFeatures = async (): Promise<
  SubscriptionFeatureListRow[]
> => {
  const res = await instance.get<{
    results: SubscriptionFeatureListRow[];
    total: number;
  }>(`${ADMIN_PREFIX}/subscriptions/features`, {
    params: { page: 1, limit: 100 }
  });

  return res.data.results ?? [];
};
